import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, X, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { AddTrackerEntryModal } from "./AddTrackerEntryModal";
import { apiService } from "@/lib/api";
import type {
  HealthMetric,
  TrackerEntryForm,
  BloodGlucoseApiResponse,
  BloodGlucoseReading,
  BloodPressureResponse,
  HeartRateResponse,
  WaistSizeResponse,
  HipSizeResponse,
  WeightResponse,
  RespiratoryRateResponse,
} from "@/types/tracker";
import type { User } from "@/types";

// Import the new modular components
import {
  TimePeriodSelector,
  DateNavigation,
  BloodGlucoseChart,
  BloodPressureChart,
  HeartRateChart,
  WaistChart,
  HipChart,
  WeightChart,
  RespiratoryRateChart,
  TemperatureChart,
  BLOOD_GLUCOSE_COLORS,
  BLOOD_GLUCOSE_FILTERS,
  BLOOD_PRESSURE_COLORS,
  CHART_COLORS,
  BLOOD_PRESSURE_CATEGORIES,
  getDateRange,
  getPeriodBounds,
  normalizeDateForPeriod,
  type TimePeriod,
} from "./charts";

interface MetricChartModalProps {
  isOpen: boolean;
  onClose: () => void;
  metric: HealthMetric | null;
  selectedPatient?: User | null;
  darkMode?: boolean;
  isCompleted?: boolean;
}

export const MetricChartModal = ({
  isOpen,
  onClose,
  metric,
  selectedPatient,
  darkMode = false,
  isCompleted = false,
}: MetricChartModalProps) => {
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>("day");
  const [dayDate, setDayDate] = useState(() =>
    normalizeDateForPeriod("day", new Date())
  );
  const [weekDate, setWeekDate] = useState(() =>
    normalizeDateForPeriod("week", new Date())
  );
  const [monthDate, setMonthDate] = useState(() =>
    normalizeDateForPeriod("month", new Date())
  );
  const [yearDate, setYearDate] = useState(() =>
    normalizeDateForPeriod("year", new Date())
  );

  // Helper function to get the current date for the selected period
  const getCurrentDateForPeriod = (period: TimePeriod): Date => {
    switch (period) {
      case "day":
        return dayDate;
      case "week":
        return weekDate;
      case "month":
        return monthDate;
      case "year":
        return yearDate;
      default:
        return dayDate;
    }
  };

  // Helper function to update the date for a specific period
  const updateDateForPeriod = (period: TimePeriod, newDate: Date) => {
    switch (period) {
      case "day":
        setDayDate(normalizeDateForPeriod("day", newDate));
        break;
      case "week":
        setWeekDate(normalizeDateForPeriod("week", newDate));
        break;
      case "month":
        setMonthDate(normalizeDateForPeriod("month", newDate));
        break;
      case "year":
        setYearDate(normalizeDateForPeriod("year", newDate));
        break;
    }
  };

  // Common function to format date display for all trackers
  const formatDateDisplay = (period: TimePeriod): string => {
    const currentDate = getCurrentDateForPeriod(period);
    switch (period) {
      case "day":
        return currentDate.toLocaleDateString("en-US", {
          day: "numeric",
          month: "long",
          year: "numeric",
        });
      case "week": {
        const { start, end } = getPeriodBounds("week", currentDate);
        return `${start.toLocaleDateString("en-US", { day: "numeric", month: "short" })} - ${end.toLocaleDateString("en-US", { day: "numeric", month: "short" })}`;
      }
      case "month":
        return currentDate.toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        });
      case "year":
        return currentDate.getFullYear().toString();
      default:
        return currentDate.toLocaleDateString("en-US", {
          day: "numeric",
          month: "long",
          year: "numeric",
        });
    }
  };

  const [isPeriodChanging, setIsPeriodChanging] = useState(false);
  const [isAddEntryModalOpen, setIsAddEntryModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedDataPointIndex, setSelectedDataPointIndex] = useState<
    number | null
  >(null);
  const [selectedReadingId, setSelectedReadingId] = useState<string | null>(
    null
  );
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState<"success" | "error">(
    "success"
  );

  // New validation snackbar state
  const [validationSnackbar, setValidationSnackbar] = useState<{
    show: boolean;
    category: "error" | "warning" | "info";
    title: string;
    description: string;
  }>({
    show: false,
    category: "info",
    title: "",
    description: "",
  });
  const [dataPoints, setDataPoints] = useState<any[]>([]);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showDeletionErrorDialog, setShowDeletionErrorDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Handle period change with immediate loading state
  const handlePeriodChange = (newPeriod: TimePeriod) => {
    setIsPeriodChanging(true);
    setSelectedPeriod(newPeriod);

    // Reset to default date for the new period if it hasn't been set yet
    const currentDateForNewPeriod = getCurrentDateForPeriod(newPeriod);
    if (
      currentDateForNewPeriod.getTime() ===
      getCurrentDateForPeriod(selectedPeriod).getTime()
    ) {
      // If the date is the same, it means we haven't navigated in this period yet
      // Set it to the appropriate default for the new period
      const now = normalizeDateForPeriod(newPeriod, new Date());
      switch (newPeriod) {
        case "day":
          setDayDate(now);
          break;
        case "week":
          setWeekDate(now);
          break;
        case "month":
          setMonthDate(now);
          break;
        case "year":
          setYearDate(now);
          break;
      }
    }
  };

  // Handle date change with immediate loading state
  const handleDateChange = (newDate: Date) => {
    setIsPeriodChanging(true);
    updateDateForPeriod(selectedPeriod, newDate);
  };

  // Blood glucose specific state
  const [bloodGlucoseData, setBloodGlucoseData] = useState<
    BloodGlucoseReading[]
  >([]);
  const [isLoadingBloodGlucose, setIsLoadingBloodGlucose] = useState(false);
  const [bloodGlucoseError, setBloodGlucoseError] = useState<string | null>(
    null
  );
  const [yAxisConfig, setYAxisConfig] = useState({
    min: 0,
    max: 300,
    referenceRange: { min: 70, max: 140 },
  });
  const [selectedFilters, setSelectedFilters] = useState<string[]>(["All"]);

  // Blood pressure specific state
  const [bloodPressureData, setBloodPressureData] =
    useState<BloodPressureResponse | null>(null);
  const [isLoadingBloodPressure, setIsLoadingBloodPressure] = useState(false);
  const [bloodPressureError, setBloodPressureError] = useState<string | null>(
    null
  );
  const [isSystolic, setIsSystolic] = useState(true); // Toggle between systolic and diastolic
  const [systolicYAxis, setSystolicYAxis] = useState({
    min: 60,
    max: 240,
    referenceRange: { min: 90, max: 120 },
  });
  const [diastolicYAxis, setDiastolicYAxis] = useState({
    min: 30,
    max: 160,
    referenceRange: { min: 60, max: 80 },
  });

  // Heart rate specific state
  const [heartRateData, setHeartRateData] = useState<HeartRateResponse | null>(
    null
  );
  const [isLoadingHeartRate, setIsLoadingHeartRate] = useState(false);
  const [heartRateError, setHeartRateError] = useState<string | null>(null);
  const [heartRateYAxis, setHeartRateYAxis] = useState({
    min: 60,
    max: 200,
    referenceRange: { min: 70, max: 100 },
  });

  // Waist size specific state
  const [waistSizeData, setWaistSizeData] = useState<WaistSizeResponse | null>(
    null
  );
  const [isLoadingWaistSize, setIsLoadingWaistSize] = useState(false);
  const [waistSizeError, setWaistSizeError] = useState<string | null>(null);
  const [waistSizeYAxis, setWaistSizeYAxis] = useState({
    min: 0,
    max: 120,
    referenceRange: { min: 60, max: 90 },
  });

  // Hip size specific state
  const [hipSizeData, setHipSizeData] = useState<HipSizeResponse | null>(null);
  const [isLoadingHipSize, setIsLoadingHipSize] = useState(false);
  const [hipSizeError, setHipSizeError] = useState<string | null>(null);
  const [hipSizeYAxis, setHipSizeYAxis] = useState({
    min: 0,
    max: 120,
    referenceRange: { min: 60, max: 90 },
  });

  // Weight specific state
  const [weightData, setWeightData] = useState<WeightResponse | null>(null);
  const [isLoadingWeight, setIsLoadingWeight] = useState(false);
  const [weightError, setWeightError] = useState<string | null>(null);
  const [weightYAxis, setWeightYAxis] = useState({
    min: 50,
    max: 100,
    referenceRange: { min: 70, max: 74 },
  });

  // Respiratory rate specific state
  const [respiratoryRateData, setRespiratoryRateData] =
    useState<RespiratoryRateResponse | null>(null);
  const [isLoadingRespiratoryRate, setIsLoadingRespiratoryRate] =
    useState(false);
  const [respiratoryRateError, setRespiratoryRateError] = useState<
    string | null
  >(null);
  const [respiratoryRateYAxis, setRespiratoryRateYAxis] = useState({
    min: 10,
    max: 30,
    referenceRange: { min: 12, max: 20 },
  });

  // Temperature specific state
  const [temperatureData, setTemperatureData] = useState<any | null>(null);
  const [isLoadingTemperature, setIsLoadingTemperature] = useState(false);
  const [temperatureError, setTemperatureError] = useState<string | null>(null);
  const [temperatureYAxis, setTemperatureYAxis] = useState({
    min: 90,
    max: 110,
    referenceRange: { min: 97, max: 99 },
  });

  // Function to fetch blood glucose data
  const fetchBloodGlucoseData = async () => {
    if (!selectedPatient?.id || metric?.id !== "blood-glucose") {
      return;
    }
    setIsLoadingBloodGlucose(true);
    setBloodGlucoseError(null);

    try {
      // Calculate date range based on selected period and current date
      const { startDate, endDate } = getDateRange(
        selectedPeriod,
        getCurrentDateForPeriod(selectedPeriod)
      );

      const response = await apiService.getBloodGlucoseData(
        selectedPatient.id,
        startDate,
        endDate
      );

      const apiResponse: BloodGlucoseApiResponse = response.data;

      setBloodGlucoseData(apiResponse.data);
      setYAxisConfig(apiResponse.y_axis);
    } catch (error) {
      console.error("❌ Error fetching blood glucose data:", error);
      setBloodGlucoseError("Failed to load blood glucose data");
    } finally {
      setIsLoadingBloodGlucose(false);
    }
  };

  // Function to fetch blood pressure data
  const fetchBloodPressureData = async () => {
    if (!selectedPatient?.id || metric?.id !== "blood-pressure") {
      return;
    }
    setIsLoadingBloodPressure(true);
    setBloodPressureError(null);

    try {
      // Calculate date range based on selected period and current date
      const { startDate, endDate } = getDateRange(
        selectedPeriod,
        getCurrentDateForPeriod(selectedPeriod)
      );

      const response = await apiService.getBloodPressureData(
        selectedPatient.id,
        startDate,
        endDate
      );

      const apiResponse: BloodPressureResponse = response.data;

      setBloodPressureData(apiResponse);
      setSystolicYAxis(apiResponse.systolic.y_axis);
      setDiastolicYAxis(apiResponse.diastolic.y_axis);
    } catch (error) {
      console.error("❌ Error fetching blood pressure data:", error);
      setBloodPressureError("Failed to load blood pressure data");
    } finally {
      setIsLoadingBloodPressure(false);
    }
  };

  // Function to fetch heart rate data
  const fetchHeartRateData = async () => {
    if (!selectedPatient?.id || metric?.id !== "heart-rate") {
      return;
    }
    setIsLoadingHeartRate(true);
    setHeartRateError(null);

    try {
      // Calculate date range based on selected period and current date
      const { startDate, endDate } = getDateRange(
        selectedPeriod,
        getCurrentDateForPeriod(selectedPeriod)
      );

      const response = await apiService.getHeartRateData(
        selectedPatient.id,
        startDate,
        endDate
      );

      const apiResponse: HeartRateResponse = response.data;

      setHeartRateData(apiResponse);
      setHeartRateYAxis(apiResponse.y_axis);
    } catch (error) {
      console.error("❌ Error fetching heart rate data:", error);
      setHeartRateError("Failed to load heart rate data");
    } finally {
      setIsLoadingHeartRate(false);
    }
  };

  // Function to fetch waist size data
  const fetchWaistSizeData = async () => {
    if (!selectedPatient?.id || metric?.id !== "waist-size") {
      return;
    }
    setIsLoadingWaistSize(true);
    setWaistSizeError(null);

    try {
      // Calculate date range based on selected period and current date
      const currentDateForPeriod = getCurrentDateForPeriod(selectedPeriod);
      const { startDate, endDate } = getDateRange(
        selectedPeriod,
        currentDateForPeriod
      );

      const response = await apiService.getWaistSizeData(
        selectedPatient.id,
        startDate,
        endDate
      );

      const apiResponse: WaistSizeResponse = response.data;

      setWaistSizeData(apiResponse);
      setWaistSizeYAxis(apiResponse.y_axis);
    } catch (error) {
      setWaistSizeError("Failed to load waist size data");
    } finally {
      setIsLoadingWaistSize(false);
    }
  };

  // Function to fetch hip size data
  const fetchHipSizeData = async () => {
    if (!selectedPatient?.id || metric?.id !== "hip") {
      return;
    }
    setIsLoadingHipSize(true);
    setHipSizeError(null);

    try {
      // Calculate date range based on selected period and current date
      const currentDateForPeriod = getCurrentDateForPeriod(selectedPeriod);
      const { startDate, endDate } = getDateRange(
        selectedPeriod,
        currentDateForPeriod
      );

      const response = await apiService.getHipSizeData(
        selectedPatient.id,
        startDate,
        endDate
      );

      const apiResponse: HipSizeResponse = response.data;

      setHipSizeData(apiResponse);
      setHipSizeYAxis(apiResponse.y_axis);
    } catch (error) {
      setHipSizeError("Failed to load hip size data");
    } finally {
      setIsLoadingHipSize(false);
    }
  };

  // Function to fetch weight data
  const fetchWeightData = async () => {
    if (!selectedPatient?.id || metric?.id !== "weight") {
      return;
    }
    setIsLoadingWeight(true);
    setWeightError(null);

    try {
      // Calculate date range based on selected period and current date
      const currentDateForPeriod = getCurrentDateForPeriod(selectedPeriod);
      const { startDate, endDate } = getDateRange(
        selectedPeriod,
        currentDateForPeriod
      );

      const response = await apiService.getWeightData(
        selectedPatient.id,
        startDate,
        endDate
      );

      const apiResponse: WeightResponse = response.data;

      setWeightData(apiResponse);
      setWeightYAxis(apiResponse.y_axis);
    } catch (error) {
      setWeightError("Failed to load weight data");
    } finally {
      setIsLoadingWeight(false);
    }
  };

  // Function to fetch respiratory rate data
  const fetchRespiratoryRateData = async () => {
    if (!selectedPatient?.id || metric?.id !== "respiratory-rate") {
      return;
    }
    setIsLoadingRespiratoryRate(true);
    setRespiratoryRateError(null);

    try {
      // Calculate date range based on selected period and current date
      const currentDateForPeriod = getCurrentDateForPeriod(selectedPeriod);
      const { startDate, endDate } = getDateRange(
        selectedPeriod,
        currentDateForPeriod
      );

      const response = await apiService.getRespiratoryRateData(
        selectedPatient.id,
        startDate,
        endDate
      );

      const apiResponse: RespiratoryRateResponse = response.data;

      setRespiratoryRateData(apiResponse);
      setRespiratoryRateYAxis(apiResponse.y_axis);
    } catch (error) {
      setRespiratoryRateError("Failed to load respiratory rate data");
    } finally {
      setIsLoadingRespiratoryRate(false);
    }
  };

  // Function to fetch temperature data
  const fetchTemperatureData = async () => {
    if (!selectedPatient?.id || metric?.id !== "temperature") {
      return;
    }
    setIsLoadingTemperature(true);
    setTemperatureError(null);

    try {
      const currentDateForPeriod = getCurrentDateForPeriod(selectedPeriod);
      const { startDate, endDate } = getDateRange(
        selectedPeriod,
        currentDateForPeriod
      );

      const response = await apiService.getTemperatureData(
        selectedPatient.id,
        startDate,
        endDate
      );

      const apiResponse = response.data;

      setTemperatureData(apiResponse);
      setTemperatureYAxis(apiResponse.y_axis);
    } catch (error) {
      setTemperatureError("Failed to load temperature data");
    } finally {
      setIsLoadingTemperature(false);
    }
  };

  // Handle filter toggle
  const toggleFilter = (filter: string) => {
    if (filter === "All") {
      setSelectedFilters(["All"]);
    } else {
      const newFilters = selectedFilters.includes("All")
        ? [filter]
        : selectedFilters.includes(filter)
          ? selectedFilters.filter((f) => f !== filter)
          : [...selectedFilters, filter];

      // If no filters selected, default to 'All'
      if (newFilters.length === 0) {
        setSelectedFilters(["All"]);
      } else {
        setSelectedFilters(newFilters);
      }
    }
  };

  // Initialize dataPoints state when modal opens and fetch data
  useEffect(() => {
    const currentDateForPeriod = getCurrentDateForPeriod(selectedPeriod);

    if (isOpen && metric) {
      // Clear existing data immediately when period changes to prevent flash
      if (metric.id === "blood-glucose") {
        setBloodGlucoseData([]);
        // Fetch blood glucose data from API
        fetchBloodGlucoseData();
      } else if (metric.id === "blood-pressure") {
        setBloodPressureData(null);
        // Fetch blood pressure data from API

        fetchBloodPressureData();
      } else if (metric.id === "heart-rate") {
        setHeartRateData(null);
        // Fetch heart rate data from API

        fetchHeartRateData();
      } else if (metric.id === "waist-size") {
        setWaistSizeData(null);
        // Fetch waist size data from API

        fetchWaistSizeData();
      } else if (metric.id === "hip") {
        setHipSizeData(null);
        // Fetch hip size data from API

        fetchHipSizeData();
      } else if (metric.id === "weight") {
        setWeightData(null);
        // Fetch weight data from API

        fetchWeightData();
      } else if (metric.id === "respiratory-rate") {
        setRespiratoryRateData(null);
        // Fetch respiratory rate data from API

        fetchRespiratoryRateData();
      } else if (metric.id === "temperature") {
        setTemperatureData(null);

        fetchTemperatureData();
      } else if (selectedPeriod === "day") {
        // No mock data - use empty array
        setDataPoints([]);
      } else {
        setDataPoints([]);
      }
    }
  }, [
    isOpen,
    selectedPeriod,
    metric?.id,
    selectedPatient?.id,
    dayDate,
    weekDate,
    monthDate,
    yearDate,
  ]);

  // Reset period changing state when data is loaded
  useEffect(() => {
    if (
      !isLoadingBloodGlucose &&
      !isLoadingBloodPressure &&
      !isLoadingHeartRate &&
      !isLoadingWaistSize &&
      !isLoadingHipSize &&
      !isLoadingWeight &&
      !isLoadingRespiratoryRate &&
      !isLoadingTemperature
    ) {
      setIsPeriodChanging(false);
    }
  }, [
    isLoadingBloodGlucose,
    isLoadingBloodPressure,
    isLoadingHeartRate,
    isLoadingWaistSize,
    isLoadingHipSize,
    isLoadingWeight,
    isLoadingRespiratoryRate,
    isLoadingTemperature,
  ]);

  // Clear data immediately when period changes to prevent flash
  useEffect(() => {
    if (isPeriodChanging) {
      // Clear all data immediately to prevent flash of old data
      setBloodGlucoseData([]);
      setBloodPressureData(null);
      setHeartRateData(null);
      setWaistSizeData(null);
      setHipSizeData(null);
      setWeightData(null);
      setRespiratoryRateData(null);
      setTemperatureData(null);
      setDataPoints([]);
    }
  }, [isPeriodChanging]);

  // Refetch blood pressure data when toggle changes
  useEffect(() => {
    if (isOpen && metric?.id === "blood-pressure" && bloodPressureData) {
      // Refetch data when toggle changes to update chart
      fetchBloodPressureData();
    }
  }, [isSystolic, metric?.id]);

  // Reset filters when metric changes
  useEffect(() => {
    if (metric?.id === "blood-glucose") {
      setSelectedFilters(["All"]);
    }
  }, [metric?.id]);

  const chartData =
    metric?.id === "blood-glucose"
      ? bloodGlucoseData
      : metric?.id === "blood-pressure"
        ? bloodPressureData
        : metric?.id === "heart-rate"
          ? heartRateData
          : metric?.id === "waist-size"
            ? waistSizeData
            : metric?.id === "hip"
              ? hipSizeData
              : metric?.id === "weight"
                ? weightData
                : metric?.id === "respiratory-rate"
                  ? respiratoryRateData
                  : metric?.id === "temperature"
                    ? temperatureData
                    : [];

  const dayDataPoints = selectedPeriod === "day" ? dataPoints : [];
  const hasData =
    selectedPeriod === "day"
      ? Array.isArray(chartData) && chartData.length > 0
      : metric?.id === "blood-glucose"
        ? bloodGlucoseData.length > 0
        : metric?.id === "blood-pressure"
          ? bloodPressureData !== null
          : metric?.id === "heart-rate"
            ? heartRateData !== null
            : metric?.id === "waist-size"
              ? waistSizeData !== null
              : metric?.id === "hip"
                ? hipSizeData !== null
                : metric?.id === "weight"
                  ? weightData !== null
                  : metric?.id === "respiratory-rate"
                    ? respiratoryRateData !== null
                    : metric?.id === "temperature"
                      ? temperatureData !== null
                      : bloodGlucoseData.length > 0; // For week/month/year, show chart if we have any API data

  const handleAddData = () => {
    setIsAddEntryModalOpen(true);
  };

  const handleAddEntrySubmit = (data: TrackerEntryForm) => {
    // You can add logic here to handle the submitted data
    // For example, refresh the chart data
  };

  const handleDataSubmitted = () => {
    // Refetch data based on metric type
    if (metric?.id === "blood-pressure") {
      fetchBloodPressureData();
    } else if (metric?.id === "blood-glucose") {
      fetchBloodGlucoseData();
    } else if (metric?.id === "heart-rate") {
      fetchHeartRateData();
    } else if (metric?.id === "waist-size") {
      fetchWaistSizeData();
    } else if (metric?.id === "hip") {
      fetchHipSizeData();
    } else if (metric?.id === "weight") {
      fetchWeightData();
    } else if (metric?.id === "respiratory-rate") {
      fetchRespiratoryRateData();
    } else if (metric?.id === "temperature") {
      fetchTemperatureData();
    }

    // Show success snackbar
    setSnackbarMessage("Data saved successfully");
    setSnackbarType("success");
    setShowSnackbar(true);

    // Hide snackbar after 3 seconds
    setTimeout(() => {
      setShowSnackbar(false);
    }, 3000);
  };

  const handleDataSubmissionError = (
    errorMessage: string = "Failed to save data"
  ) => {
    // Show error snackbar
    setSnackbarMessage(errorMessage);
    setSnackbarType("error");
    setShowSnackbar(true);

    // Hide snackbar after 3 seconds
    setTimeout(() => {
      setShowSnackbar(false);
    }, 3000);
  };

  const handleValidationResponse = (validation: {
    category: "error" | "warning" | "info";
    title: string;
    description: string;
  }) => {
    setValidationSnackbar({
      show: true,
      category: validation.category,
      title: validation.title,
      description: validation.description,
    });

    // Hide snackbar after 5 seconds
    setTimeout(() => {
      setValidationSnackbar((prev) => ({ ...prev, show: false }));
    }, 5000);
  };

  const handleAddEntryClose = () => {
    setIsAddEntryModalOpen(false);
  };

  const handleDeleteClick = (index: number, readingId?: string) => {
    setSelectedDataPointIndex(index);
    setSelectedReadingId(readingId || null);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedDataPointIndex !== null) {
      setIsDeleting(true);

      try {
        // Handle weight data deletion
        if (
          metric?.id === "weight" &&
          selectedReadingId &&
          weightData &&
          selectedPatient?.id
        ) {
          await apiService.deleteWeightReading(
            selectedReadingId,
            selectedPatient.id
          );

          // Update local state by removing the deleted reading
          const updatedData = {
            ...weightData,
            data: weightData.data.filter(
              (reading) => reading.id !== selectedReadingId
            ),
          };
          setWeightData(updatedData);

          // Show success message
          setShowSnackbar(true);
          setSnackbarMessage("Weight reading deleted successfully");
          setSnackbarType("success");
        } else if (
          metric?.id === "blood-glucose" &&
          selectedReadingId &&
          bloodGlucoseData &&
          selectedPatient?.id
        ) {
          // Handle blood glucose data deletion
          await apiService.deleteBloodGlucoseReading(
            selectedReadingId,
            selectedPatient.id
          );

          // Update local state by removing the deleted reading
          const updatedData = bloodGlucoseData.filter(
            (reading) => reading.id !== selectedReadingId
          );
          setBloodGlucoseData(updatedData);

          // Show success message
          setShowSnackbar(true);
          setSnackbarMessage("Blood glucose reading deleted successfully");
          setSnackbarType("success");
        } else if (
          metric?.id === "blood-pressure" &&
          selectedReadingId &&
          bloodPressureData &&
          selectedPatient?.id
        ) {
          // Handle blood pressure data deletion
          await apiService.deleteBloodPressureReading(
            selectedReadingId,
            selectedPatient.id
          );

          // Update local state by removing the deleted reading from both systolic and diastolic
          const updatedSystolicData = {
            ...bloodPressureData.systolic,
            data: bloodPressureData.systolic.data.filter(
              (reading) => reading.id !== selectedReadingId
            ),
          };
          const updatedDiastolicData = {
            ...bloodPressureData.diastolic,
            data: bloodPressureData.diastolic.data.filter(
              (reading) => reading.id !== selectedReadingId
            ),
          };

          setBloodPressureData({
            systolic: updatedSystolicData,
            diastolic: updatedDiastolicData,
          });

          // Show success message
          setShowSnackbar(true);
          setSnackbarMessage("Blood pressure reading deleted successfully");
          setSnackbarType("success");
        } else if (
          metric?.id === "heart-rate" &&
          selectedReadingId &&
          heartRateData &&
          selectedPatient?.id
        ) {
          // Handle heart rate data deletion
          await apiService.deleteHeartRateReading(
            selectedReadingId,
            selectedPatient.id
          );

          // Update local state by removing the deleted reading
          const updatedData = {
            ...heartRateData,
            data: heartRateData.data.filter(
              (reading) => reading.id !== selectedReadingId
            ),
          };
          setHeartRateData(updatedData);

          // Show success message
          setShowSnackbar(true);
          setSnackbarMessage("Heart rate reading deleted successfully");
          setSnackbarType("success");
        } else if (
          metric?.id === "temperature" &&
          selectedReadingId &&
          temperatureData &&
          selectedPatient?.id
        ) {
          // Handle temperature data deletion
          await apiService.deleteTemperatureReading(
            selectedReadingId,
            selectedPatient.id
          );

          // Update local state by removing the deleted reading
          const updatedData = {
            ...temperatureData,
            data: temperatureData.data.filter(
              (reading) => reading.id !== selectedReadingId
            ),
          };
          setTemperatureData(updatedData);

          // Show success message
          setShowSnackbar(true);
          setSnackbarMessage("Temperature reading deleted successfully");
          setSnackbarType("success");
        } else if (
          metric?.id === "respiratory-rate" &&
          selectedReadingId &&
          respiratoryRateData &&
          selectedPatient?.id
        ) {
          // Handle respiratory rate data deletion
          await apiService.deleteRespiratoryRateReading(
            selectedReadingId,
            selectedPatient.id
          );

          // Update local state by removing the deleted reading
          const updatedData = {
            ...respiratoryRateData,
            data: respiratoryRateData.data.filter(
              (reading) => reading.id !== selectedReadingId
            ),
          };
          setRespiratoryRateData(updatedData);

          // Show success message
          setShowSnackbar(true);
          setSnackbarMessage("Respiratory rate reading deleted successfully");
          setSnackbarType("success");
        } else if (
          metric?.id === "hip" &&
          selectedReadingId &&
          hipSizeData &&
          selectedPatient?.id
        ) {
          // Handle hip size data deletion
          await apiService.deleteHipSizeReading(
            selectedReadingId,
            selectedPatient.id
          );

          // Update local state by removing the deleted reading
          const updatedData = {
            ...hipSizeData,
            data: hipSizeData.data.filter(
              (reading) => reading.id !== selectedReadingId
            ),
          };
          setHipSizeData(updatedData);

          // Show success message
          setShowSnackbar(true);
          setSnackbarMessage("Hip size reading deleted successfully");
          setSnackbarType("success");
        } else if (
          metric?.id === "waist-size" &&
          selectedReadingId &&
          waistSizeData &&
          selectedPatient?.id
        ) {
          // Handle waist size data deletion
          await apiService.deleteWaistSizeReading(
            selectedReadingId,
            selectedPatient.id
          );

          // Update local state by removing the deleted reading
          const updatedData = {
            ...waistSizeData,
            data: waistSizeData.data.filter(
              (reading) => reading.id !== selectedReadingId
            ),
          };
          setWaistSizeData(updatedData);

          // Show success message
          setShowSnackbar(true);
          setSnackbarMessage("Waist size reading deleted successfully");
          setSnackbarType("success");
        } else {
          // Handle mock data deletion (existing logic)
          const newDataPoints = dataPoints.filter(
            (_, index) => index !== selectedDataPointIndex
          );
          setDataPoints(newDataPoints);
          setShowSnackbar(true);
          setSnackbarMessage("The selected data has been deleted");
          setSnackbarType("success");
        }

        // Hide snackbar after 3 seconds
        setTimeout(() => {
          setShowSnackbar(false);
        }, 3000);
      } catch (error: any) {
        console.error("Delete error:", error);

        // Check if it's a 403 error (permission issue)
        if (error.status === 403) {
          setShowDeletionErrorDialog(true);
        } else {
          // Show generic error snackbar
          setShowSnackbar(true);
          setSnackbarMessage(error.message || "Failed to delete weight data");
          setSnackbarType("error");

          // Hide snackbar after 3 seconds
          setTimeout(() => {
            setShowSnackbar(false);
          }, 3000);
        }
      } finally {
        setIsDeleting(false);
        setDeleteConfirmOpen(false);
        setSelectedDataPointIndex(null);
        setSelectedReadingId(null);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setSelectedDataPointIndex(null);
    setSelectedReadingId(null);
  };

  const handleDeletionErrorDialogClose = () => {
    setShowDeletionErrorDialog(false);
  };

  if (!metric) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={cn(
          "w-[92vw] max-w-[560px] max-h-[90vh] p-0 overflow-hidden [&>button]:hidden flex flex-col rounded-2xl",
          darkMode && "dark bg-background border-border"
        )}
        aria-describedby="metric-chart-description"
      >
        {/* Hidden DialogTitle for accessibility */}
        <DialogTitle className="sr-only">
          {metric ? `${metric.title} Chart` : "Health Metric Chart"}
        </DialogTitle>

        {/* Fixed Header */}
        <div
          className={cn(
            "flex items-center justify-between px-5 pt-5 pb-3 flex-shrink-0",
            darkMode && "bg-background"
          )}
        >
          <h2
            className={cn(
              "text-xl font-bold",
              darkMode ? "text-foreground" : "text-foreground"
            )}
          >
            {metric.title}
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className={cn(
              "h-7 w-7 ",
              darkMode
                ? "text-muted-foreground hover:text-foreground hover:bg-muted"
                : "text-muted-foreground hover:text-muted-foreground hover:bg-muted"
            )}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Scrollable Content */}
        <div
          className={cn(
            "flex-1 overflow-hidden flex flex-col",
            darkMode && "bg-background"
          )}
        >
          <div className="px-5 flex-1 overflow-y-auto pb-3">
            {/* Time Period Tabs */}
            <TimePeriodSelector
              selectedPeriod={selectedPeriod}
              onPeriodChange={handlePeriodChange}
              darkMode={darkMode}
            />

            {/* Date Navigation */}
            {/* Date Navigation - compact spacing to bring chart closer */}
            <DateNavigation
              currentDate={getCurrentDateForPeriod(selectedPeriod)}
              selectedPeriod={selectedPeriod}
              onDateChange={handleDateChange}
              showDatePicker={showDatePicker}
              onShowDatePickerChange={setShowDatePicker}
              darkMode={darkMode}
            />

            {/* Blood Pressure Toggle Buttons - Only show for week/month/year views */}
            {metric?.id === "blood-pressure" && selectedPeriod !== "day" && (
              <div className="flex justify-center gap-4 py-1">
                <Button
                  variant={isSystolic ? "default" : "outline"}
                  size="sm"
                  className={cn(
                    "rounded-full px-4 py-2 text-sm font-medium",
                    isSystolic
                      ? "bg-primary text-primary-foreground hover:bg-primary/90 border-primary"
                      : darkMode
                        ? "bg-muted text-muted-foreground border-border hover:bg-muted"
                        : "bg-white text-muted-foreground border-border hover:bg-muted"
                  )}
                  onClick={() => setIsSystolic(true)}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded"
                      style={{
                        backgroundColor: BLOOD_PRESSURE_COLORS.systolic,
                      }}
                    />
                    Systolic
                  </div>
                </Button>
                <Button
                  variant={!isSystolic ? "default" : "outline"}
                  size="sm"
                  className={cn(
                    "rounded-full px-4 py-2 text-sm font-medium",
                    !isSystolic
                      ? "bg-primary text-primary-foreground hover:bg-primary/90 border-primary"
                      : darkMode
                        ? "bg-muted text-muted-foreground border-border hover:bg-muted"
                        : "bg-white text-muted-foreground border-border hover:bg-muted"
                  )}
                  onClick={() => setIsSystolic(false)}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded"
                      style={{
                        backgroundColor: BLOOD_PRESSURE_COLORS.diastolic,
                      }}
                    />
                    Diastolic
                  </div>
                </Button>
              </div>
            )}

            {/* Chart Content */}
            <div className="mt-1">
              {isLoadingBloodGlucose && metric?.id === "blood-glucose" ? (
                <div
                  className={cn(
                    "h-72 rounded-lg border flex items-center justify-center",
                    darkMode
                      ? "bg-muted border-border"
                      : "bg-white border-border"
                  )}
                >
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                    <div
                      className={cn(
                        "text-sm",
                        darkMode
                          ? "text-muted-foreground"
                          : "text-muted-foreground"
                      )}
                    >
                      Loading blood glucose data...
                    </div>
                  </div>
                </div>
              ) : bloodGlucoseError && metric?.id === "blood-glucose" ? (
                <div
                  className={cn(
                    "h-72 rounded-lg border flex items-center justify-center text-destructive",
                    darkMode
                      ? "bg-muted border-border"
                      : "bg-white border-border"
                  )}
                >
                  <div className="text-center">
                    <div className="text-lg mb-2">Error</div>
                    <div className="text-sm">{bloodGlucoseError}</div>
                  </div>
                </div>
              ) : isLoadingBloodPressure && metric?.id === "blood-pressure" ? (
                <div
                  className={cn(
                    "h-72 rounded-lg border flex items-center justify-center",
                    darkMode
                      ? "bg-muted border-border"
                      : "bg-white border-border"
                  )}
                >
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                    <div
                      className={cn(
                        "text-sm",
                        darkMode
                          ? "text-muted-foreground"
                          : "text-muted-foreground"
                      )}
                    >
                      Loading blood pressure data...
                    </div>
                  </div>
                </div>
              ) : bloodPressureError && metric?.id === "blood-pressure" ? (
                <div
                  className={cn(
                    "h-72 rounded-lg border flex items-center justify-center text-destructive",
                    darkMode
                      ? "bg-muted border-border"
                      : "bg-white border-border"
                  )}
                >
                  <div className="text-center">
                    <div className="text-lg mb-2">Error</div>
                    <div className="text-sm">{bloodPressureError}</div>
                  </div>
                </div>
              ) : isLoadingHeartRate && metric?.id === "heart-rate" ? (
                <div
                  className={cn(
                    "h-72 rounded-lg border flex items-center justify-center",
                    darkMode
                      ? "bg-muted border-border"
                      : "bg-white border-border"
                  )}
                >
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                    <div
                      className={cn(
                        "text-sm",
                        darkMode
                          ? "text-muted-foreground"
                          : "text-muted-foreground"
                      )}
                    >
                      Loading heart rate data...
                    </div>
                  </div>
                </div>
              ) : heartRateError && metric?.id === "heart-rate" ? (
                <div
                  className={cn(
                    "h-72 rounded-lg border flex items-center justify-center text-destructive",
                    darkMode
                      ? "bg-muted border-border"
                      : "bg-white border-border"
                  )}
                >
                  <div className="text-center">
                    <div className="text-lg mb-2">Error</div>
                    <div className="text-sm">{heartRateError}</div>
                  </div>
                </div>
              ) : isLoadingWaistSize && metric?.id === "waist-size" ? (
                <div
                  className={cn(
                    "h-72 rounded-lg border flex items-center justify-center",
                    darkMode
                      ? "bg-muted border-border"
                      : "bg-white border-border"
                  )}
                >
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                    <div
                      className={cn(
                        "text-sm",
                        darkMode
                          ? "text-muted-foreground"
                          : "text-muted-foreground"
                      )}
                    >
                      Loading waist size data...
                    </div>
                  </div>
                </div>
              ) : waistSizeError && metric?.id === "waist-size" ? (
                <div
                  className={cn(
                    "h-72 rounded-lg border flex items-center justify-center text-destructive",
                    darkMode
                      ? "bg-muted border-border"
                      : "bg-white border-border"
                  )}
                >
                  <div className="text-center">
                    <div className="text-lg mb-2">Error</div>
                    <div className="text-sm">{waistSizeError}</div>
                  </div>
                </div>
              ) : isLoadingHipSize && metric?.id === "hip" ? (
                <div
                  className={cn(
                    "h-72 rounded-lg border flex items-center justify-center",
                    darkMode
                      ? "bg-muted border-border"
                      : "bg-white border-border"
                  )}
                >
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                    <div
                      className={cn(
                        "text-sm",
                        darkMode
                          ? "text-muted-foreground"
                          : "text-muted-foreground"
                      )}
                    >
                      Loading hip size data...
                    </div>
                  </div>
                </div>
              ) : hipSizeError && metric?.id === "hip" ? (
                <div
                  className={cn(
                    "h-72 rounded-lg border flex items-center justify-center text-destructive",
                    darkMode
                      ? "bg-muted border-border"
                      : "bg-white border-border"
                  )}
                >
                  <div className="text-center">
                    <div className="text-lg mb-2">Error</div>
                    <div className="text-sm">{hipSizeError}</div>
                  </div>
                </div>
              ) : isLoadingWeight && metric?.id === "weight" ? (
                <div
                  className={cn(
                    "h-72 rounded-lg border flex items-center justify-center",
                    darkMode
                      ? "bg-muted border-border"
                      : "bg-white border-border"
                  )}
                >
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                    <div
                      className={cn(
                        "text-sm",
                        darkMode
                          ? "text-muted-foreground"
                          : "text-muted-foreground"
                      )}
                    >
                      Loading weight data...
                    </div>
                  </div>
                </div>
              ) : weightError && metric?.id === "weight" ? (
                <div
                  className={cn(
                    "h-72 rounded-lg border flex items-center justify-center text-destructive",
                    darkMode
                      ? "bg-muted border-border"
                      : "bg-white border-border"
                  )}
                >
                  <div className="text-center">
                    <div className="text-lg mb-2">Error</div>
                    <div className="text-sm">{weightError}</div>
                  </div>
                </div>
              ) : isLoadingRespiratoryRate &&
                metric?.id === "respiratory-rate" ? (
                <div
                  className={cn(
                    "h-72 rounded-lg border flex items-center justify-center",
                    darkMode
                      ? "bg-muted border-border"
                      : "bg-white border-border"
                  )}
                >
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                    <div
                      className={cn(
                        "text-sm",
                        darkMode
                          ? "text-muted-foreground"
                          : "text-muted-foreground"
                      )}
                    >
                      Loading respiratory rate data...
                    </div>
                  </div>
                </div>
              ) : respiratoryRateError && metric?.id === "respiratory-rate" ? (
                <div
                  className={cn(
                    "h-72 rounded-lg border flex items-center justify-center text-destructive",
                    darkMode
                      ? "bg-muted border-border"
                      : "bg-white border-border"
                  )}
                >
                  <div className="text-center">
                    <div className="text-lg mb-2">Error</div>
                    <div className="text-sm">{respiratoryRateError}</div>
                  </div>
                </div>
              ) : isLoadingTemperature && metric?.id === "temperature" ? (
                <div
                  className={cn(
                    "h-72 rounded-lg border flex items-center justify-center",
                    darkMode
                      ? "bg-muted border-border"
                      : "bg-white border-border"
                  )}
                >
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                    <div
                      className={cn(
                        "text-sm",
                        darkMode
                          ? "text-muted-foreground"
                          : "text-muted-foreground"
                      )}
                    >
                      Loading temperature data...
                    </div>
                  </div>
                </div>
              ) : temperatureError && metric?.id === "temperature" ? (
                <div
                  className={cn(
                    "h-72 rounded-lg border flex items-center justify-center text-destructive",
                    darkMode
                      ? "bg-muted border-border"
                      : "bg-white border-border"
                  )}
                >
                  <div className="text-center">
                    <div className="text-lg mb-2">Error</div>
                    <div className="text-sm">{temperatureError}</div>
                  </div>
                </div>
              ) : (
                <div
                  className={cn(
                    "h-72 p-2 rounded-xl border",
                    darkMode
                      ? "bg-muted border-border"
                      : "bg-white border-border"
                  )}
                >
                  {(() => {
                    const isLoading =
                      isPeriodChanging ||
                      (metric?.id === "blood-glucose" &&
                        isLoadingBloodGlucose) ||
                      (metric?.id === "blood-pressure" &&
                        isLoadingBloodPressure) ||
                      (metric?.id === "heart-rate" && isLoadingHeartRate) ||
                      (metric?.id === "waist-size" && isLoadingWaistSize) ||
                      (metric?.id === "hip" && isLoadingHipSize) ||
                      (metric?.id === "weight" && isLoadingWeight) ||
                      (metric?.id === "respiratory-rate" &&
                        isLoadingRespiratoryRate) ||
                      (metric?.id === "temperature" && isLoadingTemperature);

                    if (isLoading) {
                      return (
                        <div className="w-full h-full flex items-center justify-center">
                          <div className="text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                            <div
                              className={cn(
                                "text-sm",
                                darkMode
                                  ? "text-muted-foreground"
                                  : "text-muted-foreground"
                              )}
                            >
                              Loading chart data...
                            </div>
                          </div>
                        </div>
                      );
                    }

                    return metric?.id === "blood-glucose" ? (
                      <BloodGlucoseChart
                        data={bloodGlucoseData}
                        selectedPeriod={selectedPeriod}
                        currentDate={getCurrentDateForPeriod(selectedPeriod)}
                        yAxisConfig={yAxisConfig}
                        selectedFilters={selectedFilters}
                        darkMode={darkMode}
                      />
                    ) : metric?.id === "blood-pressure" ? (
                      <BloodPressureChart
                        data={bloodPressureData}
                        selectedPeriod={selectedPeriod}
                        currentDate={getCurrentDateForPeriod(selectedPeriod)}
                        systolicYAxis={systolicYAxis}
                        diastolicYAxis={diastolicYAxis}
                        isSystolic={isSystolic}
                        darkMode={darkMode}
                      />
                    ) : metric?.id === "heart-rate" ? (
                      <HeartRateChart
                        data={heartRateData}
                        selectedPeriod={selectedPeriod}
                        currentDate={getCurrentDateForPeriod(selectedPeriod)}
                        yAxisConfig={heartRateYAxis}
                        darkMode={darkMode}
                      />
                    ) : metric?.id === "waist-size" ? (
                      (() => {
                        const currentDate =
                          getCurrentDateForPeriod(selectedPeriod);

                        return (
                          <WaistChart
                            data={waistSizeData}
                            selectedPeriod={selectedPeriod}
                            currentDate={currentDate}
                            darkMode={darkMode}
                          />
                        );
                      })()
                    ) : metric?.id === "hip" ? (
                      (() => {
                        const currentDate =
                          getCurrentDateForPeriod(selectedPeriod);
                        return (
                          <HipChart
                            data={hipSizeData}
                            selectedPeriod={selectedPeriod}
                            currentDate={currentDate}
                            darkMode={darkMode}
                          />
                        );
                      })()
                    ) : metric?.id === "weight" ? (
                      (() => {
                        const currentDate =
                          getCurrentDateForPeriod(selectedPeriod);

                        return (
                          <WeightChart
                            data={weightData}
                            selectedPeriod={selectedPeriod}
                            currentDate={currentDate}
                            darkMode={darkMode}
                          />
                        );
                      })()
                    ) : metric?.id === "respiratory-rate" ? (
                      (() => {
                        const currentDate =
                          getCurrentDateForPeriod(selectedPeriod);
                        return (
                          <RespiratoryRateChart
                            data={respiratoryRateData}
                            selectedPeriod={selectedPeriod}
                            currentDate={currentDate}
                            darkMode={darkMode}
                          />
                        );
                      })()
                    ) : metric?.id === "temperature" ? (
                      (() => {
                        const currentDate =
                          getCurrentDateForPeriod(selectedPeriod);
                        return (
                          <TemperatureChart
                            data={temperatureData}
                            selectedPeriod={selectedPeriod}
                            currentDate={currentDate}
                            darkMode={darkMode}
                          />
                        );
                      })()
                    ) : (
                      // Regular chart for other metrics using mock data
                      <div className="w-full h-full flex items-center justify-center text-foreground0">
                        Chart for {metric.title} - Mock data available
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Blood Glucose Specific Features */}
            {metric?.id === "blood-glucose" && (
              <>
                {/* Filter Buttons - Only show for week/month/year views */}
                {selectedPeriod !== "day" && (
                  <div className="flex flex-wrap gap-2 justify-center mb-4">
                    {BLOOD_GLUCOSE_FILTERS.map((filter) => (
                      <Button
                        key={filter}
                        variant={
                          selectedFilters.includes(filter)
                            ? "default"
                            : "outline"
                        }
                        size="sm"
                        className={cn(
                          "rounded-full px-3 py-1 text-xs",
                          selectedFilters.includes(filter)
                            ? "bg-primary text-primary-foreground hover:bg-primary/90"
                            : darkMode
                              ? "bg-muted text-muted-foreground border-border hover:bg-muted"
                              : "bg-white text-muted-foreground border-border hover:bg-muted"
                        )}
                        onClick={() => toggleFilter(filter)}
                      >
                        <div className="flex items-center gap-2">
                          {filter !== "All" && (
                            <div
                              className="w-2 h-2 rounded-full"
                              style={{
                                backgroundColor: BLOOD_GLUCOSE_COLORS[filter],
                              }}
                            />
                          )}
                          {filter}
                        </div>
                      </Button>
                    ))}
                  </div>
                )}

                {/* Legend for Day View - since filters are hidden */}
                {selectedPeriod === "day" && (
                  <div className="flex flex-wrap gap-2 justify-center mb-4">
                    {Object.entries(BLOOD_GLUCOSE_COLORS).map(
                      ([label, color]) => (
                        <div
                          key={label}
                          className={cn(
                            "flex items-center gap-1.5 px-2 py-1 rounded-full border",
                            darkMode
                              ? "bg-muted border-border"
                              : "bg-muted border-border"
                          )}
                        >
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: color }}
                          />
                          <span
                            className={cn(
                              "text-xs font-medium",
                              darkMode
                                ? "text-muted-foreground"
                                : "text-muted-foreground"
                            )}
                          >
                            {label}
                          </span>
                        </div>
                      )
                    )}
                  </div>
                )}
              </>
            )}

            {/* Blood Pressure Specific Features */}
            {metric?.id === "blood-pressure" && (
              <>
                {/* Category Legend - Only show for day view */}
                {selectedPeriod === "day" && (
                  <div className="flex flex-wrap gap-2 justify-center mb-4">
                    {BLOOD_PRESSURE_CATEGORIES.map((category) => (
                      <div
                        key={category.label}
                        className="flex items-center gap-1"
                      >
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: category.color }}
                        />
                        <span
                          className={cn(
                            "text-xs",
                            darkMode
                              ? "text-muted-foreground"
                              : "text-muted-foreground"
                          )}
                        >
                          {category.label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Systolic/Diastolic Legend - Show for all views */}
                <div className="flex flex-wrap gap-4 justify-center mb-4">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded"
                      style={{
                        backgroundColor: BLOOD_PRESSURE_COLORS.systolic,
                      }}
                    />
                    <span
                      className={cn(
                        "text-xs",
                        darkMode
                          ? "text-muted-foreground"
                          : "text-muted-foreground"
                      )}
                    >
                      Systolic
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded"
                      style={{
                        backgroundColor: BLOOD_PRESSURE_COLORS.diastolic,
                      }}
                    />
                    <span
                      className={cn(
                        "text-xs",
                        darkMode
                          ? "text-muted-foreground"
                          : "text-muted-foreground"
                      )}
                    >
                      Diastolic
                    </span>
                  </div>
                </div>
              </>
            )}

            {/* Data Points List - Show for all time periods */}
            <div className="mt-3">
              {metric?.id === "blood-glucose" ? (
                // Blood glucose data from API
                <>
                  <div
                    className={cn(
                      "text-sm",
                      darkMode
                        ? "text-muted-foreground"
                        : "text-muted-foreground"
                    )}
                  >
                    <div
                      className={cn(
                        "font-medium",
                        darkMode ? "text-foreground" : "text-foreground"
                      )}
                    >
                      {formatDateDisplay(selectedPeriod)}
                    </div>
                  </div>
                  <div
                    className={cn(
                      "text-sm mb-3",
                      darkMode
                        ? "text-muted-foreground"
                        : "text-muted-foreground"
                    )}
                  >
                    {bloodGlucoseData.length} Data
                  </div>

                  {bloodGlucoseData.length > 0 ? (
                    /* Scrollable Blood Glucose Data Points */
                    <div className="max-h-32 overflow-y-auto space-y-2">
                      {bloodGlucoseData.map((reading, index) => (
                        <div
                          key={reading.id}
                          className={cn(
                            "flex items-center justify-between py-1 px-2 rounded-lg",
                            darkMode ? "bg-muted" : "bg-muted"
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full flex-shrink-0"
                              style={{
                                backgroundColor:
                                  BLOOD_GLUCOSE_COLORS[
                                    reading.sample_type.name
                                  ] || "#gray",
                              }}
                            />
                            <div className="flex flex-col">
                              <div
                                className={cn(
                                  "text-sm font-medium",
                                  darkMode
                                    ? "text-foreground"
                                    : "text-foreground"
                                )}
                              >
                                {selectedPeriod === "day"
                                  ? reading.time
                                  : reading.date}
                              </div>
                              <div
                                className={cn(
                                  "text-sm font-bold",
                                  darkMode
                                    ? "text-foreground"
                                    : "text-foreground"
                                )}
                              >
                                {reading.value} {reading.sample_unit}
                              </div>
                              <div
                                className={cn(
                                  "text-xs",
                                  darkMode
                                    ? "text-muted-foreground"
                                    : "text-foreground0"
                                )}
                              >
                                {reading.sample_type.name}
                              </div>
                            </div>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className={cn(
                                  "h-6 w-6",
                                  darkMode
                                    ? "text-muted-foreground hover:text-foreground"
                                    : "text-muted-foreground hover:text-muted-foreground"
                                )}
                              >
                                <MoreHorizontal className="h-3 w-3" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-32">
                              <DropdownMenuItem
                                onClick={() =>
                                  handleDeleteClick(index, reading.id)
                                }
                                className="text-destructive focus:text-destructive cursor-pointer"
                              >
                                Delete
                              </DropdownMenuItem>
                              <DropdownMenuItem className="cursor-pointer">
                                Cancel
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </>
              ) : metric?.id === "blood-pressure" ? (
                // Blood pressure data from API
                <>
                  <div
                    className={cn(
                      "text-sm",
                      darkMode
                        ? "text-muted-foreground"
                        : "text-muted-foreground"
                    )}
                  >
                    <div
                      className={cn(
                        "font-medium",
                        darkMode ? "text-foreground" : "text-foreground"
                      )}
                    >
                      {formatDateDisplay(selectedPeriod)}
                    </div>
                  </div>

                  <div
                    className={cn(
                      "text-sm mb-3",
                      darkMode
                        ? "text-muted-foreground"
                        : "text-muted-foreground"
                    )}
                  >
                    {bloodPressureData
                      ? isSystolic
                        ? bloodPressureData.systolic.data.length
                        : bloodPressureData.diastolic.data.length
                      : 0}{" "}
                    Data
                  </div>

                  {bloodPressureData &&
                  (isSystolic
                    ? bloodPressureData.systolic.data.length > 0
                    : bloodPressureData.diastolic.data.length > 0) ? (
                    /* Scrollable Blood Pressure Data Points */
                    <div className="max-h-32 overflow-y-auto space-y-2">
                      {(isSystolic
                        ? bloodPressureData.systolic.data
                        : bloodPressureData.diastolic.data
                      ).map((reading, index) => (
                        <div
                          key={reading.id}
                          className={cn(
                            "flex items-center justify-between py-1 px-2 rounded-lg",
                            darkMode ? "bg-muted" : "bg-muted"
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded flex-shrink-0"
                              style={{
                                backgroundColor: isSystolic
                                  ? BLOOD_PRESSURE_COLORS.systolic
                                  : BLOOD_PRESSURE_COLORS.diastolic,
                              }}
                            />
                            <div className="flex flex-col">
                              <div
                                className={cn(
                                  "text-sm font-medium",
                                  darkMode
                                    ? "text-foreground"
                                    : "text-foreground"
                                )}
                              >
                                {selectedPeriod === "day"
                                  ? reading.time
                                  : reading.date}
                              </div>
                              <div
                                className={cn(
                                  "text-sm font-bold",
                                  darkMode
                                    ? "text-foreground"
                                    : "text-foreground"
                                )}
                              >
                                {selectedPeriod === "day"
                                  ? // Show both systolic and diastolic for day view
                                    (() => {
                                      const counterpart = isSystolic
                                        ? bloodPressureData!.diastolic.data.find(
                                            (r) =>
                                              r.date === reading.date &&
                                              r.time === reading.time
                                          )
                                        : bloodPressureData!.systolic.data.find(
                                            (r) =>
                                              r.date === reading.date &&
                                              r.time === reading.time
                                          );

                                      const systolicValue = isSystolic
                                        ? reading.value
                                        : counterpart
                                          ? counterpart.value
                                          : "?";
                                      const diastolicValue = isSystolic
                                        ? counterpart
                                          ? counterpart.value
                                          : "?"
                                        : reading.value;

                                      return `${systolicValue}-${diastolicValue} ${metric.unit}`;
                                    })()
                                  : // Show individual value for other periods
                                    `${reading.value} ${metric.unit}`}
                              </div>
                              <div
                                className={cn(
                                  "text-xs",
                                  darkMode
                                    ? "text-muted-foreground"
                                    : "text-foreground0"
                                )}
                              >
                                {selectedPeriod === "day"
                                  ? "Systolic-Diastolic"
                                  : isSystolic
                                    ? "Systolic"
                                    : "Diastolic"}
                              </div>
                            </div>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className={cn(
                                  "h-6 w-6",
                                  darkMode
                                    ? "text-muted-foreground hover:text-foreground"
                                    : "text-muted-foreground hover:text-muted-foreground"
                                )}
                              >
                                <MoreHorizontal className="h-3 w-3" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-32">
                              <DropdownMenuItem
                                onClick={() =>
                                  handleDeleteClick(index, reading.id)
                                }
                                className="text-destructive focus:text-destructive cursor-pointer"
                              >
                                Delete
                              </DropdownMenuItem>
                              <DropdownMenuItem className="cursor-pointer">
                                Cancel
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </>
              ) : metric?.id === "heart-rate" ? (
                // Heart rate data from API
                <>
                  <div
                    className={cn(
                      "text-sm",
                      darkMode
                        ? "text-muted-foreground"
                        : "text-muted-foreground"
                    )}
                  >
                    <div
                      className={cn(
                        "font-medium",
                        darkMode ? "text-foreground" : "text-foreground"
                      )}
                    >
                      {formatDateDisplay(selectedPeriod)}
                    </div>
                  </div>
                  <div
                    className={cn(
                      "text-sm mb-3",
                      darkMode
                        ? "text-muted-foreground"
                        : "text-muted-foreground"
                    )}
                  >
                    {heartRateData && heartRateData.data.length > 0
                      ? `${heartRateData.data.length} Data`
                      : "No Data"}
                  </div>

                  {heartRateData && heartRateData.data.length > 0 ? (
                    /* Scrollable Heart Rate Data Points */
                    <div className="max-h-32 overflow-y-auto space-y-2">
                      {heartRateData.data.map((reading, index) => (
                        <div
                          key={reading.id}
                          className={cn(
                            "flex items-center justify-between py-1 px-2 rounded-lg",
                            darkMode ? "bg-muted" : "bg-muted"
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded flex-shrink-0"
                              style={{
                                backgroundColor: CHART_COLORS.heartRate,
                              }}
                            />
                            <div className="flex flex-col">
                              <div
                                className={cn(
                                  "text-sm font-medium",
                                  darkMode
                                    ? "text-foreground"
                                    : "text-foreground"
                                )}
                              >
                                {selectedPeriod === "day"
                                  ? reading.time
                                  : reading.date}
                              </div>
                              <div
                                className={cn(
                                  "text-sm font-bold",
                                  darkMode
                                    ? "text-foreground"
                                    : "text-foreground"
                                )}
                              >
                                {reading.value} {metric.unit}
                              </div>
                              <div
                                className={cn(
                                  "text-xs",
                                  darkMode
                                    ? "text-muted-foreground"
                                    : "text-foreground0"
                                )}
                              >
                                Heart Rate
                              </div>
                            </div>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className={cn(
                                  "h-6 w-6",
                                  darkMode
                                    ? "text-muted-foreground hover:text-foreground"
                                    : "text-muted-foreground hover:text-muted-foreground"
                                )}
                              >
                                <MoreHorizontal className="h-3 w-3" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-32">
                              <DropdownMenuItem
                                onClick={() =>
                                  handleDeleteClick(index, reading.id)
                                }
                                className="text-destructive focus:text-destructive cursor-pointer"
                              >
                                Delete
                              </DropdownMenuItem>
                              <DropdownMenuItem className="cursor-pointer">
                                Cancel
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </>
              ) : metric?.id === "waist-size" ? (
                // Waist size data from API
                <>
                  <div
                    className={cn(
                      "text-sm",
                      darkMode
                        ? "text-muted-foreground"
                        : "text-muted-foreground"
                    )}
                  >
                    <div
                      className={cn(
                        "font-medium",
                        darkMode ? "text-foreground" : "text-foreground"
                      )}
                    >
                      {formatDateDisplay(selectedPeriod)}
                    </div>
                  </div>
                  <div
                    className={cn(
                      "text-sm mb-3",
                      darkMode
                        ? "text-muted-foreground"
                        : "text-muted-foreground"
                    )}
                  >
                    {waistSizeData && waistSizeData.data.length > 0
                      ? `${waistSizeData.data.length} Data`
                      : "No Data"}
                  </div>

                  {waistSizeData && waistSizeData.data.length > 0 ? (
                    /* Scrollable Waist Size Data Points */
                    <div className="max-h-32 overflow-y-auto space-y-2">
                      {waistSizeData.data.map((reading, index) => (
                        <div
                          key={reading.id}
                          className={cn(
                            "flex items-center justify-between py-1 px-2 rounded-lg",
                            darkMode ? "bg-muted" : "bg-muted"
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded flex-shrink-0"
                              style={{
                                backgroundColor: CHART_COLORS.bloodGlucose,
                              }}
                            />
                            <div className="flex flex-col">
                              <div
                                className={cn(
                                  "text-sm font-medium",
                                  darkMode
                                    ? "text-foreground"
                                    : "text-foreground"
                                )}
                              >
                                {selectedPeriod === "day"
                                  ? reading.time
                                  : reading.date}
                              </div>
                              <div
                                className={cn(
                                  "text-sm font-bold",
                                  darkMode
                                    ? "text-foreground"
                                    : "text-foreground"
                                )}
                              >
                                {reading.value} {metric.unit}
                              </div>
                              <div
                                className={cn(
                                  "text-xs",
                                  darkMode
                                    ? "text-muted-foreground"
                                    : "text-foreground0"
                                )}
                              >
                                Waist Size
                              </div>
                            </div>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className={cn(
                                  "h-6 w-6",
                                  darkMode
                                    ? "text-muted-foreground hover:text-foreground"
                                    : "text-muted-foreground hover:text-muted-foreground"
                                )}
                              >
                                <MoreHorizontal className="h-3 w-3" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-32">
                              <DropdownMenuItem
                                onClick={() =>
                                  handleDeleteClick(index, reading.id)
                                }
                                className="text-destructive focus:text-destructive cursor-pointer"
                              >
                                Delete
                              </DropdownMenuItem>
                              <DropdownMenuItem className="cursor-pointer">
                                Cancel
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </>
              ) : metric?.id === "hip" ? (
                // Hip size data from API
                <>
                  <div
                    className={cn(
                      "text-sm",
                      darkMode
                        ? "text-muted-foreground"
                        : "text-muted-foreground"
                    )}
                  >
                    <div
                      className={cn(
                        "font-medium",
                        darkMode ? "text-foreground" : "text-foreground"
                      )}
                    >
                      {formatDateDisplay(selectedPeriod)}
                    </div>
                  </div>
                  <div
                    className={cn(
                      "text-sm mb-3",
                      darkMode
                        ? "text-muted-foreground"
                        : "text-muted-foreground"
                    )}
                  >
                    {hipSizeData && hipSizeData.data.length > 0
                      ? `${hipSizeData.data.length} Data`
                      : "No Data"}
                  </div>

                  {hipSizeData && hipSizeData.data.length > 0 ? (
                    /* Scrollable Hip Size Data Points */
                    <div className="max-h-32 overflow-y-auto space-y-2">
                      {hipSizeData.data.map((reading, index) => (
                        <div
                          key={reading.id}
                          className={cn(
                            "flex items-center justify-between py-1 px-2 rounded-lg",
                            darkMode ? "bg-muted" : "bg-muted"
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded flex-shrink-0"
                              style={{
                                backgroundColor: CHART_COLORS.bloodGlucose,
                              }}
                            />
                            <div className="flex flex-col">
                              <div
                                className={cn(
                                  "text-sm font-medium",
                                  darkMode
                                    ? "text-foreground"
                                    : "text-foreground"
                                )}
                              >
                                {selectedPeriod === "day"
                                  ? reading.time
                                  : reading.date}
                              </div>
                              <div
                                className={cn(
                                  "text-sm font-bold",
                                  darkMode
                                    ? "text-foreground"
                                    : "text-foreground"
                                )}
                              >
                                {reading.value} {metric.unit}
                              </div>
                              <div
                                className={cn(
                                  "text-xs",
                                  darkMode
                                    ? "text-muted-foreground"
                                    : "text-foreground0"
                                )}
                              >
                                Hip Size
                              </div>
                            </div>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className={cn(
                                  "h-6 w-6",
                                  darkMode
                                    ? "text-muted-foreground hover:text-foreground"
                                    : "text-muted-foreground hover:text-muted-foreground"
                                )}
                              >
                                <MoreHorizontal className="h-3 w-3" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-32">
                              <DropdownMenuItem
                                onClick={() =>
                                  handleDeleteClick(index, reading.id)
                                }
                                className="text-destructive focus:text-destructive cursor-pointer"
                              >
                                Delete
                              </DropdownMenuItem>
                              <DropdownMenuItem className="cursor-pointer">
                                Cancel
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </>
              ) : metric?.id === "weight" ? (
                // Weight data from API
                <>
                  <div
                    className={cn(
                      "text-sm",
                      darkMode
                        ? "text-muted-foreground"
                        : "text-muted-foreground"
                    )}
                  >
                    <div
                      className={cn(
                        "font-medium",
                        darkMode ? "text-foreground" : "text-foreground"
                      )}
                    >
                      {formatDateDisplay(selectedPeriod)}
                    </div>
                  </div>
                  <div
                    className={cn(
                      "text-sm mb-3",
                      darkMode
                        ? "text-muted-foreground"
                        : "text-muted-foreground"
                    )}
                  >
                    {weightData && weightData.data.length > 0
                      ? `${weightData.data.length} Data`
                      : "No Data"}
                  </div>

                  {weightData && weightData.data.length > 0 ? (
                    /* Scrollable Weight Data Points */
                    <div className="max-h-32 overflow-y-auto space-y-2">
                      {weightData.data.map((reading, index) => (
                        <div
                          key={reading.id}
                          className={cn(
                            "flex items-center justify-between py-1 px-2 rounded-lg",
                            darkMode ? "bg-muted" : "bg-muted"
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded flex-shrink-0"
                              style={{
                                backgroundColor: CHART_COLORS.temperature,
                              }}
                            />
                            <div className="flex flex-col">
                              <div
                                className={cn(
                                  "text-sm font-medium",
                                  darkMode
                                    ? "text-foreground"
                                    : "text-foreground"
                                )}
                              >
                                {selectedPeriod === "day"
                                  ? reading.time
                                  : reading.date}
                              </div>
                              <div
                                className={cn(
                                  "text-sm font-bold",
                                  darkMode
                                    ? "text-foreground"
                                    : "text-foreground"
                                )}
                              >
                                {reading.value} {metric.unit}
                              </div>
                              <div
                                className={cn(
                                  "text-xs",
                                  darkMode
                                    ? "text-muted-foreground"
                                    : "text-foreground0"
                                )}
                              >
                                Weight
                              </div>
                            </div>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className={cn(
                                  "h-6 w-6",
                                  darkMode
                                    ? "text-muted-foreground hover:text-foreground"
                                    : "text-muted-foreground hover:text-muted-foreground"
                                )}
                              >
                                <MoreHorizontal className="h-3 w-3" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-32">
                              <DropdownMenuItem
                                onClick={() =>
                                  handleDeleteClick(index, reading.id)
                                }
                                className="text-destructive focus:text-destructive cursor-pointer"
                              >
                                Delete
                              </DropdownMenuItem>
                              <DropdownMenuItem className="cursor-pointer">
                                Cancel
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </>
              ) : metric?.id === "respiratory-rate" ? (
                // Respiratory rate data from API
                <>
                  <div
                    className={cn(
                      "text-sm",
                      darkMode
                        ? "text-muted-foreground"
                        : "text-muted-foreground"
                    )}
                  >
                    <div
                      className={cn(
                        "font-medium",
                        darkMode ? "text-foreground" : "text-foreground"
                      )}
                    >
                      {formatDateDisplay(selectedPeriod)}
                    </div>
                  </div>
                  <div
                    className={cn(
                      "text-sm mb-3",
                      darkMode
                        ? "text-muted-foreground"
                        : "text-muted-foreground"
                    )}
                  >
                    {respiratoryRateData && respiratoryRateData.data.length > 0
                      ? `${respiratoryRateData.data.length} Data`
                      : "No Data"}
                  </div>

                  {respiratoryRateData &&
                  respiratoryRateData.data.length > 0 ? (
                    /* Scrollable Respiratory Rate Data Points */
                    <div className="max-h-32 overflow-y-auto space-y-2">
                      {respiratoryRateData.data.map((reading, index) => (
                        <div
                          key={reading.id}
                          className={cn(
                            "flex items-center justify-between py-1 px-2 rounded-lg",
                            darkMode ? "bg-muted" : "bg-muted"
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded flex-shrink-0"
                              style={{
                                backgroundColor: CHART_COLORS.respiratoryRate,
                              }}
                            />
                            <div className="flex flex-col">
                              <div
                                className={cn(
                                  "text-sm font-medium",
                                  darkMode
                                    ? "text-foreground"
                                    : "text-foreground"
                                )}
                              >
                                {selectedPeriod === "day"
                                  ? reading.time
                                  : reading.date}
                              </div>
                              <div
                                className={cn(
                                  "text-sm font-bold",
                                  darkMode
                                    ? "text-foreground"
                                    : "text-foreground"
                                )}
                              >
                                {reading.value} {metric.unit}
                              </div>
                              <div
                                className={cn(
                                  "text-xs",
                                  darkMode
                                    ? "text-muted-foreground"
                                    : "text-foreground0"
                                )}
                              >
                                Respiratory Rate
                              </div>
                            </div>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className={cn(
                                  "h-6 w-6",
                                  darkMode
                                    ? "text-muted-foreground hover:text-foreground"
                                    : "text-muted-foreground hover:text-muted-foreground"
                                )}
                              >
                                <MoreHorizontal className="h-3 w-3" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-32">
                              <DropdownMenuItem
                                onClick={() =>
                                  handleDeleteClick(index, reading.id)
                                }
                                className="text-destructive focus:text-destructive cursor-pointer"
                              >
                                Delete
                              </DropdownMenuItem>
                              <DropdownMenuItem className="cursor-pointer">
                                Cancel
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </>
              ) : metric?.id === "temperature" ? (
                // Temperature data from API
                <>
                  <div
                    className={cn(
                      "text-sm",
                      darkMode
                        ? "text-muted-foreground"
                        : "text-muted-foreground"
                    )}
                  >
                    <div
                      className={cn(
                        "font-medium",
                        darkMode ? "text-foreground" : "text-foreground"
                      )}
                    >
                      {formatDateDisplay(selectedPeriod)}
                    </div>
                  </div>
                  <div
                    className={cn(
                      "text-sm mb-3",
                      darkMode
                        ? "text-muted-foreground"
                        : "text-muted-foreground"
                    )}
                  >
                    {temperatureData && temperatureData.data.length > 0
                      ? `${temperatureData.data.length} Data`
                      : "No Data"}
                  </div>
                  {temperatureData && temperatureData.data.length > 0 ? (
                    <div className="max-h-32 overflow-y-auto space-y-2">
                      {temperatureData.data.map(
                        (reading: any, index: number) => (
                          <div
                            key={reading.id}
                            className={cn(
                              "flex items-center justify-between py-1 px-2 rounded-lg",
                              darkMode ? "bg-muted" : "bg-muted"
                            )}
                          >
                            <div className="flex items-center gap-2">
                              <div
                                className="w-3 h-3 rounded flex-shrink-0"
                                style={{ backgroundColor: CHART_COLORS.weight }}
                              />
                              <div className="flex flex-col">
                                <div
                                  className={cn(
                                    "text-sm font-medium",
                                    darkMode
                                      ? "text-foreground"
                                      : "text-foreground"
                                  )}
                                >
                                  {selectedPeriod === "day"
                                    ? reading.time
                                    : reading.date}
                                </div>
                                <div
                                  className={cn(
                                    "text-sm font-bold",
                                    darkMode
                                      ? "text-foreground"
                                      : "text-foreground"
                                  )}
                                >
                                  {reading.value} °F
                                </div>
                                <div
                                  className={cn(
                                    "text-xs",
                                    darkMode
                                      ? "text-muted-foreground"
                                      : "text-foreground0"
                                  )}
                                >
                                  Temperature
                                </div>
                              </div>
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className={cn(
                                    "h-6 w-6",
                                    darkMode
                                      ? "text-muted-foreground hover:text-foreground"
                                      : "text-muted-foreground hover:text-muted-foreground"
                                  )}
                                >
                                  <MoreHorizontal className="h-3 w-3" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-32">
                                <DropdownMenuItem
                                  onClick={() =>
                                    handleDeleteClick(index, reading.id)
                                  }
                                  className="text-destructive focus:text-destructive cursor-pointer"
                                >
                                  Delete
                                </DropdownMenuItem>
                                <DropdownMenuItem className="cursor-pointer">
                                  Cancel
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        )
                      )}
                    </div>
                  ) : null}
                </>
              ) : (
                // Mock data for other metrics - Only show for day view
                selectedPeriod === "day" &&
                dayDataPoints.length > 0 && (
                  <>
                    <div
                      className={cn(
                        "text-base font-semibold mb-2",
                        darkMode ? "text-foreground" : "text-foreground"
                      )}
                    >
                      {metric.value}
                    </div>
                    <div
                      className={cn(
                        "text-sm mb-3",
                        darkMode
                          ? "text-muted-foreground"
                          : "text-muted-foreground"
                      )}
                    >
                      {dayDataPoints.length} Data
                    </div>

                    {/* Scrollable Data Points */}
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {dayDataPoints.map((dataPoint, index) => (
                        <div
                          key={index}
                          className={cn(
                            "flex items-center justify-between py-1 px-2 rounded-lg",
                            darkMode ? "bg-muted" : "bg-muted"
                          )}
                        >
                          <div className="flex flex-col">
                            <div
                              className={cn(
                                "text-sm font-medium",
                                darkMode ? "text-foreground" : "text-foreground"
                              )}
                            >
                              {dataPoint.time}
                            </div>
                            <div
                              className={cn(
                                "text-sm font-bold",
                                darkMode ? "text-foreground" : "text-foreground"
                              )}
                            >
                              {dataPoint.value} {dataPoint.unit}
                            </div>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className={cn(
                                  "h-6 w-6",
                                  darkMode
                                    ? "text-muted-foreground hover:text-foreground"
                                    : "text-muted-foreground hover:text-muted-foreground"
                                )}
                              >
                                <MoreHorizontal className="h-3 w-3" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-32">
                              <DropdownMenuItem
                                onClick={() => handleDeleteClick(index)}
                                className="text-destructive focus:text-destructive cursor-pointer"
                              >
                                Delete
                              </DropdownMenuItem>
                              <DropdownMenuItem className="cursor-pointer">
                                Cancel
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      ))}
                    </div>
                  </>
                )
              )}
            </div>
          </div>
        </div>

        {/* Fixed Bottom Section */}
        {!isCompleted && (
          <div
            className={cn(
              "flex items-center justify-end px-5 py-3 flex-shrink-0",
              darkMode ? "bg-background" : "bg-white"
            )}
          >
            <Button
              onClick={handleAddData}
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-5 py-2 rounded-lg font-medium text-sm shadow-sm"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Add
            </Button>
          </div>
        )}

        {/* Add Entry Modal */}
        <AddTrackerEntryModal
          isOpen={isAddEntryModalOpen}
          onClose={handleAddEntryClose}
          metric={metric}
          onSubmit={handleAddEntrySubmit}
          selectedPatient={selectedPatient}
          onDataSubmitted={handleDataSubmitted}
          onDataSubmissionError={handleDataSubmissionError}
          onValidationResponse={handleValidationResponse}
          darkMode={darkMode}
        />

        {/* Delete Confirmation Dialog */}
        <AlertDialog
          open={deleteConfirmOpen}
          onOpenChange={setDeleteConfirmOpen}
        >
          <AlertDialogContent className="max-w-md">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-lg font-semibold text-foreground">
                Delete this record, permanently?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-sm text-muted-foreground">
                This will delete the selected record.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex justify-end space-x-2">
              <AlertDialogCancel
                onClick={handleDeleteCancel}
                className="px-4 py-2 text-muted-foreground bg-white border border-border rounded-md hover:bg-muted"
                disabled={isDeleting}
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteConfirm}
                className="px-4 py-2 text-white bg-destructive rounded-md hover:bg-destructive/90 disabled:opacity-50"
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Deletion Error Dialog */}
        <AlertDialog
          open={showDeletionErrorDialog}
          onOpenChange={setShowDeletionErrorDialog}
        >
          <AlertDialogContent className="max-w-md">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-lg font-semibold text-foreground">
                Deletion not available
              </AlertDialogTitle>
              <AlertDialogDescription className="text-sm text-muted-foreground">
                This reading is locked for deletion to maintain accurate
                records. If you need assistance, please contact your care team.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex justify-end">
              <AlertDialogAction
                onClick={handleDeletionErrorDialogClose}
                className="px-4 py-2 text-primary-foreground bg-primary rounded-md hover:bg-primary/90"
              >
                Okay
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Validation Snackbar */}
        {validationSnackbar.show && (
          <div className="fixed bottom-4 left-4 z-50 max-w-md">
            <div
              className={cn(
                "flex flex-col rounded-lg shadow-lg border",
                validationSnackbar.category === "error"
                  ? "bg-destructive/10 border-destructive/40 p-3"
                  : "p-4",
                validationSnackbar.category === "warning" &&
                  "bg-warning/10 border-warning/40",
                validationSnackbar.category === "info" &&
                  "bg-success/10 border-success/40"
              )}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4
                    className={cn(
                      "font-semibold text-sm mb-1",
                      validationSnackbar.category === "error" &&
                        "text-destructive",
                      validationSnackbar.category === "warning" &&
                        "text-warning",
                      validationSnackbar.category === "info" && "text-success"
                    )}
                  >
                    {validationSnackbar.title}
                  </h4>
                  <p
                    className={cn(
                      "text-sm",
                      validationSnackbar.category === "error" &&
                        "text-destructive",
                      validationSnackbar.category === "warning" &&
                        "text-warning",
                      validationSnackbar.category === "info" && "text-success"
                    )}
                  >
                    {validationSnackbar.description}
                  </p>
                </div>
                <button
                  onClick={() =>
                    setValidationSnackbar((prev) => ({ ...prev, show: false }))
                  }
                  className={cn(
                    "ml-3 flex-shrink-0",
                    validationSnackbar.category === "error" &&
                      "text-destructive hover:text-destructive",
                    validationSnackbar.category === "warning" &&
                      "text-warning hover:text-warning",
                    validationSnackbar.category === "info" &&
                      "text-success hover:text-success"
                  )}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
